import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { stats, teamMembers } from '@/mocks/testimonials';

const teamAvatarImages: Record<string, string> = {
  'team-tanaka': 'https://readdy.ai/api/search-image?query=Professional%20Japanese%20female%20educator%20headshot%2C%20warm%20smile%2C%20neutral%20background%2C%20soft%20lighting%2C%20elegant%20professional%20style&width=200&height=200&seq=team-tanaka&orientation=squarish',
  'team-park': 'https://readdy.ai/api/search-image?query=Professional%20Korean%20female%20educator%20headshot%2C%20warm%20smile%2C%20neutral%20background%2C%20soft%20lighting%2C%20modern%20professional%20style&width=200&height=200&seq=team-park&orientation=squarish',
  'team-yamamoto': 'https://readdy.ai/api/search-image?query=Professional%20Japanese%20male%20business%20educator%20headshot%2C%20confident%20smile%2C%20neutral%20background%2C%20soft%20lighting%2C%20professional%20style&width=200&height=200&seq=team-yamamoto&orientation=squarish',
  'team-kim': 'https://readdy.ai/api/search-image?query=Professional%20Korean%20female%20cultural%20educator%20headshot%2C%20warm%20smile%2C%20neutral%20background%2C%20soft%20lighting%2C%20approachable%20style&width=200&height=200&seq=team-kim&orientation=squarish',
};

const aboutImages = {
  hero: 'https://readdy.ai/api/search-image?query=Modern%20educational%20institution%20building%20with%20warm%20lighting%2C%20Asian%20design%20elements%2C%20clean%20minimalist%20architecture%2C%20cherry%20blossom%20trees%2C%20professional%20academic%20atmosphere%2C%20inspiring%20environment&width=1920&height=500&seq=about-hero-2026&orientation=landscape',
  mission: 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20adult%20learners%20studying%20together%20in%20modern%20classroom%2C%20warm%20lighting%2C%20collaborative%20atmosphere%2C%20minimalist%20design%2C%20Asian%20educational%20setting&width=700&height=500&seq=about-mission-2026&orientation=landscape',
};

interface FormState {
  success: boolean;
  error: string;
  loading: boolean;
}

export default function AboutPage() {
  const [formState, setFormState] = useState<FormState>({ success: false, error: '', loading: false });

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState({ success: true, error: '', loading: false });
      return;
    }

    setFormState({ success: false, error: '', loading: true });

    try {
      const url = 'https://readdy.ai/api/form/d9q875snlsngrm4llt80';
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormState({ success: true, error: '', loading: false });
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, unknown>)?.meta as Record<string, unknown> | undefined;
        const msg = (serverMsg?.message as string) || (parsed as Record<string, unknown>)?.message as string || responseText;
        setFormState({ success: false, error: msg || '提交失败，请稍后再试', loading: false });
      }
    } catch {
      setFormState({ success: false, error: '网络错误，请检查您的网络连接后重试', loading: false });
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="relative w-full h-[300px] md:h-[380px] overflow-hidden">
          <img
            src={aboutImages.hero}
            alt="关于我们"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mt-3">
                关于我们
              </h1>
            </div>
          </div>
        </div>

        <section className="py-16 md:py-20">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <span className="text-sm font-medium text-primary-600 mb-2 block">品牌故事</span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                  日韩一区二区 · 成人教育新标杆
                </h2>
                <div className="space-y-4 text-foreground-600 leading-relaxed">
                  <p>
                    日韩一区二区成人教育平台创立于2020年，是国内领先的日韩在线教育品牌。
                    我们汇聚了来自日本和韩国的顶尖教育资源，致力于为中国成人学习者提供高品质、
                    系统化的日韩语言、职场、文化及学历提升课程。
                  </p>
                  <p>
                    平台独创"分区教学"模式，将日本区（一区）和韩国区（二区）的课程体系进行精细化分类，
                    涵盖<strong>语言课程、职场培训、文化教育、学历提升、资格考试</strong>五大核心板块，
                    为不同学习需求的成人学员提供最精准的课程匹配。
                  </p>
                  <p>
                    截至目前，平台已累计服务超过<strong>50,000名</strong>注册学员，上线<strong>200+</strong>精品课程，
                    汇聚了<strong>30+</strong>位来自日本和韩国的资深讲师，学员满意度高达<strong>98%</strong>。
                  </p>
                </div>
              </div>
              <div className="flex-1">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={aboutImages.mission}
                    alt="我们的使命"
                    className="w-full h-[350px] object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-primary-500">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat) => (
                <div key={stat.id} className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-50/15 flex items-center justify-center">
                    <i className={`${stat.icon} text-2xl text-background-50`}></i>
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-background-50 mb-2">{stat.value}</p>
                  <p className="text-sm text-background-50/70">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm font-medium text-primary-600 mb-2 block">核心团队</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                资深教研团队
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto">
                来自日本和韩国的顶尖教育专家，为您提供最专业的教学服务
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className="bg-background-100 rounded-xl p-6 text-center transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden">
                    <img
                      src={teamAvatarImages[member.avatarSeq] || teamAvatarImages['team-tanaka']}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1">{member.name}</h3>
                  <p className="text-sm text-primary-600 font-medium mb-1">{member.role}</p>
                  <p className="text-xs text-foreground-500 mb-3">{member.title}</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-12">
              <div className="flex-1">
                <span className="text-sm font-medium text-primary-600 mb-2 block">联系我们</span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                  期待与您沟通
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <a href="mailto:contact@jpek-edu.com" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                        contact@jpek-edu.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">客服电话</p>
                      <a href="tel:4008889999" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                        400-888-9999
                      </a>
                      <p className="text-xs text-foreground-500 mt-1">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">公司地址</p>
                      <p className="text-sm text-foreground-600">北京市朝阳区建国路88号SOHO现代城A座15层</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="bg-background-50 rounded-xl p-6 md:p-8">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">在线留言</h3>
                  {formState.success ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                        <i className="ri-check-line text-2xl text-green-600"></i>
                      </div>
                      <p className="text-foreground-900 font-medium text-base mb-2">留言提交成功！</p>
                      <p className="text-foreground-500 text-sm">感谢您的留言，我们将在1-2个工作日内与您联系。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="您的姓名"
                          required
                          className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="您的邮箱"
                          required
                          className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="请输入您的留言内容（限500字）"
                          rows={5}
                          maxLength={500}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                      </div>

                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />

                      {formState.error && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                          <p className="text-sm text-red-600">{formState.error}</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={formState.loading}
                        className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {formState.loading ? '提交中...' : '提交留言'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}